import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { AppShell } from '@/components/app-shell';
import { useSession, SessionProvider } from '@/context/session-context';
import { subscribeAdminProspectsRealtime } from '@/lib/subscribe-admin-prospects-realtime';
import { AuthCallbackPage, LoginPage } from '@/pages/login-page';

const DashboardPage = lazy(() => import('@/pages/dashboard-page').then((module) => ({ default: module.DashboardPage })));
const SearchPage = lazy(() => import('@/pages/search-page').then((module) => ({ default: module.SearchPage })));
const UserDetailPage = lazy(() => import('@/pages/user-detail-page').then((module) => ({ default: module.UserDetailPage })));
const BusinessDetailPage = lazy(() => import('@/pages/business-detail-page').then((module) => ({ default: module.BusinessDetailPage })));
const StaffPage = lazy(() => import('@/pages/staff-page').then((module) => ({ default: module.StaffPage })));
const DomainListPage = lazy(() => import('@/pages/domain-list-page').then((module) => ({ default: module.DomainListPage })));
const ModerationPage = lazy(() => import('@/pages/domain-list-page').then((module) => ({ default: module.ModerationPage })));
const CommunicationsPage = lazy(() => import('@/pages/domain-list-page').then((module) => ({ default: module.CommunicationsPage })));
const SystemHealthPage = lazy(() => import('@/pages/domain-list-page').then((module) => ({ default: module.SystemHealthPage })));
const ProspectPreviewsPage = lazy(() => import('@/pages/prospect-previews-page').then((module) => ({ default: module.ProspectPreviewsPage })));
const CatalogDictionaryPage = lazy(() => import('@/pages/catalog-dictionary-page').then((module) => ({ default: module.CatalogDictionaryPage })));
const PublicProspectPreviewPage = lazy(() => import('@/pages/public-prospect-preview-page').then((module) => ({ default: module.PublicProspectPreviewPage })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AdminLiveSync() {
  const queryClient = useQueryClient();
  const { staffSession } = useSession();

  useEffect(() => {
    if (!staffSession) return;
    return subscribeAdminProspectsRealtime(() => {
      void queryClient.invalidateQueries({ queryKey: ['prospect-discovery'] });
      void queryClient.invalidateQueries({ queryKey: ['prospect-previews'] });
    });
  }, [queryClient, staffSession]);

  return null;
}

function RequireStaff({ children }: { children: React.ReactNode }) {
  const { authSession, staffSession, loading } = useSession();
  if (loading) return <main className="login-page"><div className="state-card"><span className="spinner" />Verifying staff access…</div></main>;
  if (!authSession || !staffSession) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RequirePlatformAdmin({ children }: { children: React.ReactNode }) {
  const { staffSession } = useSession();
  if (staffSession?.staff.role !== 'platform_admin') return <Navigate to="/" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Suspense fallback={<div className="state-card"><span className="spinner" />Loading workspace…</div>}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/preview/:token" element={<PublicProspectPreviewPage />} />
        <Route element={<RequireStaff><AppShell /></RequireStaff>}>
          <Route index element={<DashboardPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/users" element={<DomainListPage domain="users" />} />
          <Route path="/users/:userId" element={<UserDetailPage />} />
          <Route path="/businesses" element={<DomainListPage domain="businesses" />} />
          <Route path="/businesses/:businessId" element={<BusinessDetailPage />} />
          <Route path="/prospects" element={<ProspectPreviewsPage />} />
          <Route path="/dictionary" element={<CatalogDictionaryPage />} />
          <Route path="/bookings" element={<DomainListPage domain="bookings" />} />
          <Route path="/subscriptions" element={<DomainListPage domain="subscriptions" />} />
          <Route path="/moderation" element={<ModerationPage />} />
          <Route path="/communications" element={<CommunicationsPage />} />
          <Route path="/system" element={<SystemHealthPage />} />
          <Route path="/audit" element={<DomainListPage domain="audit" />} />
          <Route path="/staff" element={<RequirePlatformAdmin><StaffPage /></RequirePlatformAdmin>} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <AdminLiveSync />
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </SessionProvider>
    </QueryClientProvider>
  );
}
